import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  XCircle,
  CreditCard,
  CheckCircle2,
  Truck,
  ShoppingBag,
  Mail,
  Phone,
  Star,
  TrendingUp,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import KoupacekForm from '../components/KoupacekForm';
import { 
  festivalInfo, 
  programSchedule, 
  whyJoinReasons, 
  vendorCategories, 
  importantRules, 
  pricingConditions,
  programHighlights,
  doprovodnyProgram,
  contactInfo,
  heroImage,
  logoImage
} from '../data/koupacekData';

const KoupacekLanding = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const scrollToForm = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const iconComponents = {
    Users, Clock, Calendar, CheckCircle2, XCircle, CreditCard, MapPin, Star, TrendingUp
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-cyan-100">
      {/* Header */}
      <header className="fixed top-0 w-full bg-gradient-to-r from-cyan-500 to-blue-500 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white hover:text-cyan-100"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Přehled akcí</span>
            </Button>
            <img 
              src={logoImage}
              alt="Koupáček"
              className="h-10 md:h-12"
            />
          </div>
          <Button 
            onClick={scrollToForm}
            className="bg-white hover:bg-cyan-50 text-cyan-600 font-bold px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            PŘIHLÁSIT SE
          </Button>
        </div>
      </header>

      {/* Hero Section - Inspired by Poster Design */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-white overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt="Festival Koupáček 2026"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/90 via-blue-600/85 to-cyan-500/90"></div>
          
          {/* Decorative Elements - Bubbles/Waves */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute top-40 right-20 w-48 h-48 bg-cyan-300/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-40 w-40 h-40 bg-blue-400/20 rounded-full blur-2xl"></div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          {/* Logo - větší velikost */}
          <div className="mb-12 flex justify-center">
            <img 
              src={logoImage}
              alt="Koupáček Logo"
              className="h-32 md:h-40 lg:h-48 drop-shadow-2xl"
            />
          </div>
          
          {/* Main Title - proporcionálně menší než logo */}
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-4 leading-tight text-white drop-shadow-lg">
              FESTIVAL PRO<br/>
              CELOU RODINU
            </h1>
          </div>

          {/* Date Badge */}
          <div className="inline-block bg-white text-cyan-600 px-12 py-5 rounded-full font-black text-4xl md:text-5xl mb-8 shadow-2xl">
            5.7.2026
          </div>
          
          {/* Location */}
          <div className="text-2xl md:text-3xl mb-2 text-white font-bold uppercase tracking-wide">
            MĚSTSKÉ KOUPALIŠTĚ
          </div>
          <div className="text-4xl md:text-5xl mb-8 text-cyan-200 font-black uppercase tracking-wider">
            OTROKOVICE
          </div>

          {/* Time */}
          <div className="text-2xl md:text-3xl mb-12 text-white font-bold">
            10:00 – 19:00
          </div>

          {/* CTA Button */}
          <Button 
            onClick={scrollToForm}
            size="lg"
            className="bg-white hover:bg-cyan-50 text-cyan-600 text-xl px-16 py-8 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all transform hover:scale-105"
          >
            🏕️ PŘIHLÁSIT STÁNEK NYNÍ
          </Button>

          {/* Bottom tagline */}
          <div className="mt-12 text-cyan-100 text-xl md:text-2xl font-bold max-w-3xl mx-auto">
            Den plný hudby, her, tvoření a zábavy pro děti i jejich rodiče! 💦🎉
          </div>
        </div>
      </section>

      {/* Festival Stats */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b-4 border-cyan-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white p-8 rounded-2xl shadow-xl">
              <Calendar className="mx-auto h-12 w-12 mb-4" />
              <div className="text-4xl font-black mb-2">{festivalInfo.date}</div>
              <div className="text-cyan-100 text-sm font-semibold">Termín</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white p-8 rounded-2xl shadow-xl">
              <Clock className="mx-auto h-12 w-12 mb-4" />
              <div className="text-4xl font-black mb-2">{festivalInfo.time}</div>
              <div className="text-cyan-100 text-sm font-semibold">Čas</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-600 to-blue-500 text-white p-8 rounded-2xl shadow-xl">
              <Users className="mx-auto h-12 w-12 mb-4" />
              <div className="text-4xl font-black mb-2">{festivalInfo.expectedVisitors}+</div>
              <div className="text-cyan-100 text-sm font-semibold">Návštěvníků</div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-cyan-500 text-white p-8 rounded-2xl shadow-xl">
              <MapPin className="mx-auto h-12 w-12 mb-4" />
              <div className="text-2xl font-black mb-2">Otrokovice</div>
              <div className="text-cyan-100 text-sm font-semibold">Městské koupaliště</div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 to-blue-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-cyan-600">
              🎤 Program festivalu
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Celodenní program plný zábavy, hudby a aktivit pro celou rodinu
            </p>
          </div>

          {/* Festival Poster - Velký vizuál */}
          <div className="mb-12 flex justify-center">
            <div className="relative max-w-4xl w-full rounded-2xl overflow-hidden shadow-2xl border-4 border-cyan-400">
              <img 
                src={heroImage}
                alt="Festival Koupáček - Program 2026"
                className="w-full h-auto"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Main Stage */}
            <Card className="bg-white shadow-xl border-4 border-cyan-400">
              <CardHeader className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
                <CardTitle className="text-3xl font-black">🎪 Hlavní pódium</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  {programHighlights.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-lg">
                      <CheckCircle2 className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-800">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Supporting Program */}
            <Card className="bg-white shadow-xl border-4 border-blue-400">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                <CardTitle className="text-3xl font-black">🎈 Doprovodný program</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  {doprovodnyProgram.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-lg">
                      <CheckCircle2 className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-800">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-cyan-600">
              🍔 Proč se zúčastnit?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Výhody pro food trucky a gastro prodejce
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyJoinReasons.map((reason, index) => {
              const IconComponent = iconComponents[reason.icon];
              return (
                <Card key={index} className="bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-300 hover:shadow-2xl transition-all transform hover:scale-105">
                  <CardHeader>
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-black text-cyan-600">{reason.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">{reason.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vendor Categories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-100 to-blue-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-cyan-700">
              Koho hledáme?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Priority Vendors */}
            <Card className="bg-white shadow-2xl border-4 border-cyan-500">
              <CardHeader className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
                <div className="flex items-center gap-3">
                  <Truck className="h-8 w-8" />
                  <CardTitle className="text-3xl font-black">{vendorCategories.priority.title}</CardTitle>
                </div>
                <CardDescription className="text-cyan-100 text-lg mt-2">
                  {vendorCategories.priority.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  {vendorCategories.priority.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-cyan-500 flex-shrink-0 mt-1" />
                      <span className="text-gray-800 font-semibold">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Additional Vendors */}
            <Card className="bg-white shadow-2xl border-4 border-blue-500">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                <div className="flex items-center gap-3">
                  <ShoppingBag className="h-8 w-8" />
                  <CardTitle className="text-3xl font-black">{vendorCategories.additional.title}</CardTitle>
                </div>
                <CardDescription className="text-blue-100 text-lg mt-2">
                  {vendorCategories.additional.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <ul className="space-y-3">
                  {vendorCategories.additional.categories.map((cat, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
                      <span className="text-gray-800 font-semibold">{cat}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl font-black mb-4 text-cyan-600">
              💰 Ceník
            </h2>
            <p className="text-xl text-gray-700">
              Transparentní ceny pro všechny prodejce
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Food Trucks */}
            <Card className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-2xl border-4 border-cyan-400">
              <CardHeader>
                <CardTitle className="text-3xl font-black">{pricingConditions.foodTrucks.title}</CardTitle>
                <CardDescription className="text-cyan-100 text-lg">{pricingConditions.foodTrucks.category}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-black mb-2">{pricingConditions.foodTrucks.price}</div>
                <div className="text-cyan-100 text-lg">Velikost: {pricingConditions.foodTrucks.size}</div>
              </CardContent>
            </Card>

            {/* Additional Sales */}
            <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white shadow-2xl border-4 border-blue-400">
              <CardHeader>
                <CardTitle className="text-3xl font-black">{pricingConditions.additional.title}</CardTitle>
                <CardDescription className="text-blue-100 text-lg">{pricingConditions.additional.category}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-black mb-2">{pricingConditions.additional.price}</div>
                <div className="text-blue-100 text-lg">Velikost: {pricingConditions.additional.size}</div>
              </CardContent>
            </Card>
          </div>

          {/* Electricity & Deposit */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="bg-white shadow-xl border-2 border-cyan-300">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-cyan-600">{pricingConditions.electricity.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pricingConditions.electricity.options.map((option, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-cyan-50 rounded-lg">
                      <span className="font-bold text-gray-800">{option.range}</span>
                      <span className="text-xl font-black text-cyan-600">{option.price}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-3">{pricingConditions.electricity.note}</p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-xl border-2 border-blue-300">
              <CardHeader>
                <CardTitle className="text-2xl font-black text-blue-600">{pricingConditions.deposit.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-5xl font-black text-blue-600 mb-3">{pricingConditions.deposit.price}</div>
                <p className="text-gray-700 leading-relaxed">
                  Vratná kauce za úklid a dodržení podmínek.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center p-6 bg-gradient-to-r from-yellow-100 to-amber-100 rounded-xl border-2 border-yellow-400">
            <p className="text-xl font-bold text-gray-800">⚠️ {pricingConditions.note}</p>
          </div>
        </div>
      </section>

      {/* Important Rules */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-orange-50 border-y-4 border-red-400">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-red-600">
              ⚠️ Důležitá pravidla
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {importantRules.map((rule, index) => {
              const IconComponent = iconComponents[rule.icon];
              return (
                <Card key={index} className="bg-white shadow-xl border-4 border-red-400">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="bg-red-500 w-12 h-12 rounded-full flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-2xl font-black text-red-600">{rule.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-800 font-semibold mb-2">{rule.description}</p>
                    {rule.note && (
                      <p className="text-sm text-gray-600 italic">{rule.note}</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cyan-500 via-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-5xl md:text-6xl font-black mb-6">
            Staňte se součástí<br/>Festival Koupáček 2026!
          </h2>
          <p className="text-2xl mb-8 text-cyan-100">
            Termín uzávěrky přihlášek: <span className="font-black">10. 4. 2026</span>
          </p>
          <Button 
            onClick={scrollToForm}
            size="lg"
            className="bg-white hover:bg-cyan-50 text-cyan-600 text-2xl px-16 py-8 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all transform hover:scale-110"
          >
            🎪 PŘIHLÁSIT SE NYNÍ
          </Button>
        </div>
      </section>

      {/* Application Form */}
      {showForm && (
        <section id="application-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-5xl md:text-6xl font-black mb-4 text-cyan-600">
                📝 Přihláška prodejce
              </h2>
              <p className="text-xl text-gray-700">
                Vyplňte formulář a staňte se součástí festivalu
              </p>
            </div>
            <KoupacekForm />
          </div>
        </section>
      )}

      {/* More Events Section */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white border-y-2 sm:border-y-4 border-cyan-400">
        <div className="max-w-5xl mx-auto text-center">
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl sm:rounded-2xl p-6 sm:p-12 border-2 sm:border-4 border-cyan-300 shadow-xl">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 text-cyan-700">
              🎪 Máme více festivalů!
            </h2>
            <p className="text-base sm:text-xl text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Zajímá vás prodejní místo i na dalších akcích? Podívejte se na všechny naše festivaly v roce 2026.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-base sm:text-xl px-6 sm:px-12 py-4 sm:py-6 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all w-full sm:w-auto"
            >
              🎉 Zobrazit další akce
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 to-blue-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-cyan-600">
              📞 Kontakt
            </h2>
          </div>

          <Card className="bg-white shadow-2xl border-4 border-cyan-400">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                  <p className="font-bold text-gray-800 mb-1">Email</p>
                  <a href={`mailto:${contactInfo.email}`} className="text-cyan-600 hover:text-cyan-700 font-semibold">
                    {contactInfo.email}
                  </a>
                </div>
                <div>
                  <div className="bg-gradient-to-r from-blue-500 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <p className="font-bold text-gray-800 mb-1">Telefon</p>
                  <a href={`tel:${contactInfo.phone}`} className="text-cyan-600 hover:text-cyan-700 font-semibold">
                    {contactInfo.phone}
                  </a>
                </div>
                <div>
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <p className="font-bold text-gray-800 mb-1">Kontaktní osoba</p>
                  <p className="text-gray-700 font-semibold">{contactInfo.name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src={logoImage}
            alt="Koupáček"
            className="h-16 mx-auto mb-4"
          />
          <p className="text-cyan-100">
            © 2026 Festival Koupáček | Městské koupaliště Otrokovice
          </p>
          <p className="text-cyan-200 text-sm mt-2">
            Organizováno společností Vše pro akci
          </p>
        </div>
      </footer>
    </div>
  );
};

export default KoupacekLanding;
